import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus,
  Edit3,
  Copy,
  EyeOff,
  Eye,
  X,
  RefreshCw,
  Tag,
  Calendar,
  Percent,
} from 'lucide-react';
import type { DiscountType } from 'shared';
import { api, ApiError } from '@/lib/api';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { SkeletonCard } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

/* ==========================================================================
   S12 — Admin Promotions (Promotion Management)

   List, create, edit, duplicate, and deactivate promotions.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

type PromotionStatus = 'attiva' | 'scaduta' | 'bozza';

interface Promotion {
  id: number;
  name: string;
  discountType: DiscountType;
  discountValue: number;
  description: string;
  appliesTo: string[];
  validFrom: string;
  validTo: string;
  combinable: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PromotionFormData {
  name: string;
  discountType: DiscountType;
  discountValue: string;
  description: string;
  appliesTo: string;
  validFrom: string;
  validTo: string;
  combinable: boolean;
}

/* --------------------------------------------------------------------------
   Constants
   -------------------------------------------------------------------------- */

const DISCOUNT_TYPE_LABELS: Record<DiscountType, string> = {
  percentage: 'Percentuale',
  fixed: 'Fisso (\u20ac)',
  bundle: 'Bundle',
};

const EMPTY_FORM: PromotionFormData = {
  name: '',
  discountType: 'percentage',
  discountValue: '',
  description: '',
  appliesTo: '',
  validFrom: '',
  validTo: '',
  combinable: false,
};

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

function getPromotionStatus(promo: Promotion): PromotionStatus {
  if (!promo.active) return 'bozza';
  const now = new Date();
  const validTo = new Date(promo.validTo);
  const validFrom = new Date(promo.validFrom);
  if (now > validTo) return 'scaduta';
  if (now < validFrom) return 'bozza';
  return 'attiva';
}

function getStatusBadge(
  status: PromotionStatus,
): { status: 'positive' | 'negative' | 'neutral'; label: string } {
  switch (status) {
    case 'attiva':
      return { status: 'positive', label: 'Attiva' };
    case 'scaduta':
      return { status: 'negative', label: 'Scaduta' };
    case 'bozza':
      return { status: 'neutral', label: 'Bozza' };
  }
}

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return '\u2014';
  }
}

function formatDiscountValue(promo: Promotion): string {
  switch (promo.discountType) {
    case 'percentage':
      return `${promo.discountValue}%`;
    case 'fixed':
      return `${promo.discountValue.toFixed(2)}\u00a0\u20ac`;
    case 'bundle':
      return 'Bundle';
  }
}

function toInputDate(dateStr: string): string {
  if (!dateStr) return '';
  try {
    return new Date(dateStr).toISOString().split('T')[0];
  } catch {
    return '';
  }
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AdminPromotions() {
  const toast = useToast();

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  const [promotions, setPromotions] = useState<Promotion[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPromo, setEditingPromo] = useState<Promotion | null>(null);
  const [formData, setFormData] = useState<PromotionFormData>(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [formErrors, setFormErrors] = useState<
    Partial<Record<keyof PromotionFormData, string>>
  >({});

  // ------------------------------------------------------------------
  // Fetch promotions
  // ------------------------------------------------------------------

  const loadPromotions = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<Promotion[]>('/promotions');
      setPromotions(data);
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.message);
      } else {
        setError('Errore di connessione. Riprova.');
      }
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPromotions();
  }, [loadPromotions]);

  // ------------------------------------------------------------------
  // Sorted promotions (active first, then by date)
  // ------------------------------------------------------------------

  const sortedPromotions = useMemo(() => {
    return [...promotions].sort((a, b) => {
      const statusA = getPromotionStatus(a);
      const statusB = getPromotionStatus(b);
      const statusOrder: Record<PromotionStatus, number> = {
        attiva: 0,
        bozza: 1,
        scaduta: 2,
      };
      const orderDiff = statusOrder[statusA] - statusOrder[statusB];
      if (orderDiff !== 0) return orderDiff;
      return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
    });
  }, [promotions]);

  // ------------------------------------------------------------------
  // Modal handlers
  // ------------------------------------------------------------------

  const openCreateModal = useCallback(() => {
    setEditingPromo(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
    setIsModalOpen(true);
  }, []);

  const openEditModal = useCallback((promo: Promotion) => {
    setEditingPromo(promo);
    setFormData({
      name: promo.name,
      discountType: promo.discountType,
      discountValue: promo.discountValue.toString(),
      description: promo.description || '',
      appliesTo: promo.appliesTo?.join(', ') || '',
      validFrom: toInputDate(promo.validFrom),
      validTo: toInputDate(promo.validTo),
      combinable: promo.combinable,
    });
    setFormErrors({});
    setIsModalOpen(true);
  }, []);

  const handleDuplicate = useCallback((promo: Promotion) => {
    setEditingPromo(null);
    setFormData({
      name: `${promo.name} (copia)`,
      discountType: promo.discountType,
      discountValue: promo.discountValue.toString(),
      description: promo.description || '',
      appliesTo: promo.appliesTo?.join(', ') || '',
      validFrom: '',
      validTo: '',
      combinable: promo.combinable,
    });
    setFormErrors({});
    setIsModalOpen(true);
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingPromo(null);
    setFormData(EMPTY_FORM);
    setFormErrors({});
  }, []);

  // ------------------------------------------------------------------
  // Validate
  // ------------------------------------------------------------------

  const validateForm = useCallback((): boolean => {
    const errors: Partial<Record<keyof PromotionFormData, string>> = {};

    if (!formData.name.trim()) {
      errors.name = 'Il nome e obbligatorio';
    }
    if (
      !formData.discountValue ||
      isNaN(parseFloat(formData.discountValue)) ||
      parseFloat(formData.discountValue) <= 0
    ) {
      errors.discountValue = 'Inserisci un valore valido';
    }
    if (
      formData.discountType === 'percentage' &&
      parseFloat(formData.discountValue) > 100
    ) {
      errors.discountValue = 'La percentuale non puo superare 100';
    }
    if (!formData.validFrom) {
      errors.validFrom = 'Data inizio obbligatoria';
    }
    if (!formData.validTo) {
      errors.validTo = 'Data fine obbligatoria';
    }
    if (formData.validFrom && formData.validTo) {
      if (new Date(formData.validFrom) > new Date(formData.validTo)) {
        errors.validTo = 'La data fine deve essere successiva alla data inizio';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  // ------------------------------------------------------------------
  // Save
  // ------------------------------------------------------------------

  const handleSave = useCallback(async () => {
    if (!validateForm()) return;
    setIsSaving(true);

    try {
      const payload = {
        name: formData.name.trim(),
        discountType: formData.discountType,
        discountValue: parseFloat(formData.discountValue),
        description: formData.description.trim(),
        appliesTo: formData.appliesTo
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        validFrom: new Date(formData.validFrom).toISOString(),
        validTo: new Date(formData.validTo).toISOString(),
        combinable: formData.combinable,
      };

      if (editingPromo) {
        const updated = await api.patch<Promotion>(
          `/promotions/${editingPromo.id}`,
          payload,
        );
        setPromotions((prev) =>
          prev.map((p) => (p.id === updated.id ? updated : p)),
        );
        toast.success('Promozione aggiornata');
      } else {
        const created = await api.post<Promotion>('/promotions', payload);
        setPromotions((prev) => [...prev, created]);
        toast.success('Promozione creata');
      }

      closeModal();
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || 'Errore nel salvataggio');
      } else {
        toast.error('Errore di connessione. Riprova.');
      }
    } finally {
      setIsSaving(false);
    }
  }, [validateForm, formData, editingPromo, closeModal, toast]);

  // ------------------------------------------------------------------
  // Toggle active
  // ------------------------------------------------------------------

  const handleToggleActive = useCallback(
    async (promo: Promotion) => {
      try {
        const updated = await api.patch<Promotion>(
          `/promotions/${promo.id}`,
          { active: !promo.active },
        );
        setPromotions((prev) =>
          prev.map((p) => (p.id === updated.id ? updated : p)),
        );
        toast.success(
          updated.active ? 'Promozione riattivata' : 'Promozione disattivata',
        );
      } catch (err) {
        if (err instanceof ApiError) {
          toast.error(err.message || 'Errore nella modifica');
        } else {
          toast.error('Errore di connessione.');
        }
      }
    },
    [toast],
  );

  // ------------------------------------------------------------------
  // Update form field
  // ------------------------------------------------------------------

  const updateField = useCallback(
    (field: keyof PromotionFormData, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (formErrors[field]) {
        setFormErrors((prev) => {
          const next = { ...prev };
          delete next[field];
          return next;
        });
      }
    },
    [formErrors],
  );

  // ------------------------------------------------------------------
  // Loading skeleton
  // ------------------------------------------------------------------

  if (isLoading) {
    return (
      <AppShell header={<Header showBack title="Promozioni" />}>
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------

  if (error) {
    return (
      <AppShell header={<Header showBack title="Promozioni" />}>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className={cn(
              'mb-5 flex h-16 w-16 items-center justify-center',
              'rounded-full bg-status-negative-light',
            )}
          >
            <Tag className="h-7 w-7 text-status-negative" strokeWidth={1.5} />
          </div>
          <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
            {error}
          </p>
          <Button
            variant="secondary"
            size="md"
            className="mt-6"
            leftIcon={<RefreshCw strokeWidth={2} />}
            onClick={loadPromotions}
          >
            Riprova
          </Button>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  return (
    <AppShell
      header={
        <Header
          showBack
          title="Promozioni"
          actions={
            <Button
              size="sm"
              leftIcon={<Plus strokeWidth={2} />}
              onClick={openCreateModal}
            >
              Nuova
            </Button>
          }
        />
      }
    >
      <div className="space-y-5 pb-8">
        {/* ---- Empty state ---- */}
        {sortedPromotions.length === 0 && (
          <div className="flex flex-col items-center py-12 text-center">
            <div
              className={cn(
                'mb-4 flex h-14 w-14 items-center justify-center',
                'rounded-full bg-surface-sunken',
              )}
            >
              <Tag className="h-6 w-6 text-text-muted" strokeWidth={1.5} />
            </div>
            <p className="text-sm text-text-muted">
              Nessuna promozione configurata
            </p>
            <Button
              size="md"
              className="mt-4"
              leftIcon={<Plus strokeWidth={2} />}
              onClick={openCreateModal}
            >
              Crea la prima promozione
            </Button>
          </div>
        )}

        {/* ---- Promotion cards ---- */}
        <div className="space-y-3">
          {sortedPromotions.map((promo) => (
            <PromotionCard
              key={promo.id}
              promotion={promo}
              onEdit={() => openEditModal(promo)}
              onDuplicate={() => handleDuplicate(promo)}
              onToggleActive={() => handleToggleActive(promo)}
            />
          ))}
        </div>
      </div>

      {/* ---- Create/Edit Modal ---- */}
      {isModalOpen && (
        <PromotionModal
          isEditing={Boolean(editingPromo)}
          formData={formData}
          formErrors={formErrors}
          isSaving={isSaving}
          onUpdateField={updateField}
          onSave={handleSave}
          onClose={closeModal}
        />
      )}
    </AppShell>
  );
}

/* ==========================================================================
   Sub-components
   ========================================================================== */

/* --------------------------------------------------------------------------
   Promotion Card
   -------------------------------------------------------------------------- */

function PromotionCard({
  promotion,
  onEdit,
  onDuplicate,
  onToggleActive,
}: {
  promotion: Promotion;
  onEdit: () => void;
  onDuplicate: () => void;
  onToggleActive: () => void;
}) {
  const promoStatus = getPromotionStatus(promotion);
  const badge = getStatusBadge(promoStatus);
  const isCurrentlyActive = promoStatus === 'attiva';

  return (
    <div
      className={cn(
        'relative px-5 py-4',
        'rounded-[var(--radius-lg)] border bg-surface-card',
        'transition-all duration-[var(--duration-fast)]',
        isCurrentlyActive
          ? 'border-status-positive/30 shadow-sm'
          : 'border-border-default',
        !promotion.active && 'opacity-60',
      )}
    >
      {/* Active indicator line */}
      {isCurrentlyActive && (
        <div
          className="absolute left-0 top-3 bottom-3 w-1 rounded-r-full bg-status-positive"
          aria-hidden
        />
      )}

      {/* Header row */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h4 className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
              {promotion.name}
            </h4>
            <Badge status={badge.status} size="sm" dot>
              {badge.label}
            </Badge>
          </div>

          {promotion.description && (
            <p className="text-xs text-text-muted mt-1 line-clamp-2">
              {promotion.description}
            </p>
          )}
        </div>

        {/* Discount badge */}
        <div
          className={cn(
            'flex items-center gap-1 shrink-0',
            'px-3 py-1.5 rounded-[var(--radius-md)]',
            'bg-accent-primary-light',
          )}
        >
          <Percent className="h-3.5 w-3.5 text-accent-primary" strokeWidth={2} />
          <span className="font-[family-name:var(--font-heading)] text-sm font-bold text-accent-primary">
            {formatDiscountValue(promotion)}
          </span>
        </div>
      </div>

      {/* Details row */}
      <div className="flex items-center gap-4 mt-3 text-xs text-text-muted">
        <span className="flex items-center gap-1">
          <Calendar className="h-3.5 w-3.5" strokeWidth={1.5} />
          {formatDate(promotion.validFrom)} \u2013 {formatDate(promotion.validTo)}
        </span>
        {promotion.combinable && (
          <Badge status="info" size="sm">
            Combinabile
          </Badge>
        )}
        {promotion.appliesTo && promotion.appliesTo.length > 0 && (
          <span className="truncate">
            Applica a: {promotion.appliesTo.join(', ')}
          </span>
        )}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1 mt-3 -ml-2">
        <button
          onClick={onEdit}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2',
            'rounded-[var(--radius-md)]',
            'text-xs font-medium text-text-secondary',
            'hover:bg-surface-sunken hover:text-text-primary',
            'transition-colors cursor-pointer',
            'min-h-[36px]',
          )}
          aria-label={`Modifica ${promotion.name}`}
        >
          <Edit3 className="h-3.5 w-3.5" strokeWidth={1.5} />
          Modifica
        </button>
        <button
          onClick={onDuplicate}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2',
            'rounded-[var(--radius-md)]',
            'text-xs font-medium text-text-secondary',
            'hover:bg-surface-sunken hover:text-text-primary',
            'transition-colors cursor-pointer',
            'min-h-[36px]',
          )}
          aria-label={`Duplica ${promotion.name}`}
        >
          <Copy className="h-3.5 w-3.5" strokeWidth={1.5} />
          Duplica
        </button>
        <button
          onClick={onToggleActive}
          className={cn(
            'flex items-center gap-1.5 px-3 py-2',
            'rounded-[var(--radius-md)]',
            'text-xs font-medium',
            'transition-colors cursor-pointer',
            'min-h-[36px]',
            promotion.active
              ? 'text-text-secondary hover:text-status-negative hover:bg-status-negative-light'
              : 'text-text-secondary hover:text-status-positive hover:bg-status-positive-light',
          )}
          aria-label={
            promotion.active
              ? `Disattiva ${promotion.name}`
              : `Riattiva ${promotion.name}`
          }
        >
          {promotion.active ? (
            <>
              <EyeOff className="h-3.5 w-3.5" strokeWidth={1.5} />
              Disattiva
            </>
          ) : (
            <>
              <Eye className="h-3.5 w-3.5" strokeWidth={1.5} />
              Riattiva
            </>
          )}
        </button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Promotion Modal (Create / Edit)
   -------------------------------------------------------------------------- */

function PromotionModal({
  isEditing,
  formData,
  formErrors,
  isSaving,
  onUpdateField,
  onSave,
  onClose,
}: {
  isEditing: boolean;
  formData: PromotionFormData;
  formErrors: Partial<Record<keyof PromotionFormData, string>>;
  isSaving: boolean;
  onUpdateField: (field: keyof PromotionFormData, value: string | boolean) => void;
  onSave: () => void;
  onClose: () => void;
}) {
  return (
    <>
      {/* Overlay */}
      <div
        className={cn(
          'fixed inset-0 z-[var(--z-overlay)] bg-surface-overlay',
          'fade-in',
        )}
        onClick={onClose}
        aria-hidden
      />

      {/* Modal sheet */}
      <div
        className={cn(
          'fixed bottom-0 left-0 right-0 z-[var(--z-modal)]',
          'bg-surface-card rounded-t-[var(--radius-xl)]',
          'shadow-xl',
          'px-5 pb-8 pt-5',
          'mx-auto max-w-3xl',
          'max-h-[85dvh] overflow-y-auto',
          'fade-in',
        )}
        role="dialog"
        aria-label={isEditing ? 'Modifica promozione' : 'Nuova promozione'}
      >
        {/* Handle */}
        <div className="flex justify-center mb-4">
          <div className="h-1 w-10 rounded-full bg-border-default" />
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h3 className="font-[family-name:var(--font-heading)] text-lg font-bold text-text-primary">
            {isEditing ? 'Modifica Promozione' : 'Nuova Promozione'}
          </h3>
          <button
            onClick={onClose}
            className={cn(
              'flex items-center justify-center',
              'h-[var(--touch-min)] w-[var(--touch-min)]',
              'rounded-[var(--radius-md)]',
              'text-text-muted hover:text-text-primary',
              'hover:bg-surface-sunken',
              'transition-colors cursor-pointer',
            )}
            aria-label="Chiudi"
          >
            <X className="h-5 w-5" strokeWidth={2} />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4">
          <Input
            label="Nome promozione"
            placeholder="Es. Sconto Primavera 2026"
            value={formData.name}
            onChange={(e) => onUpdateField('name', e.target.value)}
            error={formErrors.name}
          />

          {/* Discount type select */}
          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="promo-discount-type"
              className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary"
            >
              Tipo sconto
            </label>
            <select
              id="promo-discount-type"
              value={formData.discountType}
              onChange={(e) =>
                onUpdateField('discountType', e.target.value)
              }
              className={cn(
                'h-[var(--touch-min)] w-full rounded-[var(--radius-md)]',
                'bg-surface-card border border-border-default',
                'px-4 text-base text-text-primary',
                'font-[family-name:var(--font-body)]',
                'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
                'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
                'focus:outline-none',
                'hover:border-border-hover',
              )}
            >
              {Object.entries(DISCOUNT_TYPE_LABELS).map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <Input
            label={
              formData.discountType === 'percentage'
                ? 'Sconto (%)'
                : 'Valore sconto (\u20ac)'
            }
            type="number"
            placeholder={formData.discountType === 'percentage' ? '10' : '25.00'}
            value={formData.discountValue}
            onChange={(e) => onUpdateField('discountValue', e.target.value)}
            error={formErrors.discountValue}
          />

          <div className="flex flex-col gap-1.5">
            <label
              htmlFor="promo-description"
              className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-secondary"
            >
              Descrizione
            </label>
            <textarea
              id="promo-description"
              rows={3}
              placeholder="Descrizione della promozione..."
              value={formData.description}
              onChange={(e) => onUpdateField('description', e.target.value)}
              className={cn(
                'w-full rounded-[var(--radius-md)]',
                'bg-surface-card border border-border-default',
                'px-4 py-3 text-base text-text-primary',
                'font-[family-name:var(--font-body)]',
                'placeholder:text-text-muted',
                'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
                'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
                'focus:outline-none',
                'hover:border-border-hover',
                'resize-none',
              )}
            />
          </div>

          <Input
            label="Applicabile a (servizi o categorie, separati da virgola)"
            placeholder="Es. Peeling Viso, trattamento_corpo, Laser"
            value={formData.appliesTo}
            onChange={(e) => onUpdateField('appliesTo', e.target.value)}
          />

          {/* Date range */}
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Data inizio"
              type="date"
              value={formData.validFrom}
              onChange={(e) => onUpdateField('validFrom', e.target.value)}
              error={formErrors.validFrom}
            />
            <Input
              label="Data fine"
              type="date"
              value={formData.validTo}
              onChange={(e) => onUpdateField('validTo', e.target.value)}
              error={formErrors.validTo}
            />
          </div>

          {/* Combinable toggle */}
          <label
            className={cn(
              'flex items-center gap-3 px-4 py-3',
              'rounded-[var(--radius-md)] bg-surface-sunken border border-border-default',
              'cursor-pointer select-none',
            )}
          >
            <div
              className={cn(
                'relative h-6 w-11 rounded-full shrink-0',
                'transition-colors duration-[var(--duration-normal)]',
                formData.combinable ? 'bg-accent-primary' : 'bg-border-default',
              )}
            >
              <div
                className={cn(
                  'absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm',
                  'transition-transform duration-[var(--duration-normal)]',
                  formData.combinable ? 'translate-x-[22px]' : 'translate-x-0.5',
                )}
              />
              <input
                type="checkbox"
                className="sr-only"
                checked={formData.combinable}
                onChange={(e) => onUpdateField('combinable', e.target.checked)}
              />
            </div>
            <div>
              <span className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary">
                Combinabile
              </span>
              <p className="text-xs text-text-muted mt-0.5">
                Questa promozione puo essere combinata con altre
              </p>
            </div>
          </label>
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6">
          <Button
            variant="ghost"
            size="lg"
            className="flex-1"
            onClick={onClose}
          >
            Annulla
          </Button>
          <Button
            size="lg"
            className="flex-1"
            loading={isSaving}
            onClick={onSave}
          >
            {isEditing ? 'Salva modifiche' : 'Crea promozione'}
          </Button>
        </div>
      </div>
    </>
  );
}
